// meeting-cancellation.component.ts (Updated with v2 fallback)
import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MeetingSchedulerService } from 'src/app/services/meeting-sheduler';
import { MaterialModule } from 'src/app/material.module';
import { RecaptchaV3Service } from 'src/app/services/recaptcha-v3.service';
import { RecaptchaService } from 'src/app/services/recaptcha.service'; // Add this import
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

@Component({
  selector: 'app-meeting-cancellation',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    ReactiveFormsModule, 
    MaterialModule,
    MatSnackBarModule
  ],
  templateUrl: './meeting-cancellation.component.html',
  styleUrl:'./meeting-cancellation.component.scss'
})
export class MeetingCancellationComponent implements OnInit, OnDestroy {
  meetingId: string = '';
  cancelId: string = '';
  meetingDetails: any = null;
  cancellationMessage: string = '';
  cancellationSubmitted: boolean = false;
  meetingLinkExpired: boolean = false;
  loading: boolean = true;
  cancellationForm: FormGroup;
  submitting: boolean = false;
  
  // reCAPTCHA related
  recaptchaRequired: boolean = true;
  recaptchaInitialized: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private meetingService: MeetingSchedulerService,
    private fb: FormBuilder,
    private recaptchaV3Service: RecaptchaV3Service,
    private recaptchaService: RecaptchaService, // Add this
    private snackBar: MatSnackBar
  ) {
    this.cancellationForm = this.fb.group({
      message: ['']
    });
  }

  async ngOnInit(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.meetingId = params['id'];
      this.cancelId = params['cancelScheduleId'];
      
      if (this.meetingId && this.cancelId) {
        await this.loadMeetingDetails();
        
        // Initialize reCAPTCHA v3 after meeting details are loaded
        // Meeting cancellation is always a public-facing component
        if (this.recaptchaRequired && !this.meetingLinkExpired) {
          await this.initializeRecaptcha();
        }
      } else {
        this.meetingLinkExpired = true;
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up if needed
  }

  private async initializeRecaptcha(): Promise<void> {
    try {
      // Pre-load reCAPTCHA v3 by executing it once
      await this.recaptchaV3Service.execute('page_load');
      this.recaptchaInitialized = true;
    } catch (error) {
      console.error('Failed to initialize reCAPTCHA v3:', error);
      // Don't fail the page load if reCAPTCHA fails to initialize
      // But log it and potentially disable submission
      this.recaptchaRequired = false;
    }
  }

  // Update the textarea value when the cancellationMessage changes (for backward compatibility)
  updateMessageField(): void {
    const messageValue = this.cancellationForm.get('message')?.value;
    this.cancellationMessage = messageValue || '';
  }

  async loadMeetingDetails(): Promise<void> {
    try {
      const response = await firstValueFrom(this.meetingService.getMeetingDetails({
        id: this.meetingId,
        cancelEvent: this.cancelId
      }));
      
      this.meetingDetails = response;
      this.loading = false;
    } catch (error) {
      console.error('Error loading meeting details:', error);
      this.meetingLinkExpired = true;
      this.loading = false;
    }
  }

  formatMeetingDateTime(): string {
    if (!this.meetingDetails || !this.meetingDetails.startTime) {
      return '';
    }
    
    try {
      const startDate = new Date(this.meetingDetails.startTime);
      const endDate = new Date(this.meetingDetails.endTime);
      
      const dateOptions: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      
      const timeOptions: Intl.DateTimeFormatOptions = { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true
      };
      
      const formattedDate = startDate.toLocaleDateString(undefined, dateOptions);
      const formattedStartTime = startDate.toLocaleTimeString(undefined, timeOptions);
      const formattedEndTime = endDate.toLocaleTimeString(undefined, timeOptions);
      
      // Get timezone offset in format (+01:00)
      const tzOffset = startDate.getTimezoneOffset();
      const tzHours = Math.floor(Math.abs(tzOffset) / 60);
      const tzMinutes = Math.abs(tzOffset) % 60;
      const tzSign = tzOffset <= 0 ? '+' : '-';
      const timezone = `(GMT ${tzSign}${String(tzHours).padStart(2, '0')}:${String(tzMinutes).padStart(2, '0')})`;
      
      return `${formattedDate} ${formattedStartTime} - ${formattedEndTime} ${timezone}`;
    } catch (error) {
      console.error('Error formatting date time:', error);
      return '';
    }
  }

  async confirmCancellation(): Promise<void> {
    this.submitting = true;

    try {
      // Try to submit with v3 first
      await this.submitWithRecaptcha('v3');
    } catch (error: any) {
      console.error('v3 submission failed:', error);
      
      // If v3 failed, try v2
      if (this.recaptchaRequired) {
        this.snackBar.open('Additional verification required', 'OK', {
          duration: 2000
        });

        try {
          const v2Token = await this.recaptchaService.showCaptchaModal({
            autoClose: true,
            autoCloseDelay: 500
          });

          if (v2Token) {
            await this.submitWithRecaptcha('v2', v2Token);
          } else {
            this.snackBar.open('Verification cancelled', 'OK', {
              duration: 3000,
              panelClass: 'error-snackbar'
            });
          }
        } catch (v2Error) {
          console.error('v2 submission also failed:', v2Error);
          this.snackBar.open('Failed to cancel the meeting. Please try again.', 'OK', {
            duration: 3000,
            panelClass: 'error-snackbar'
          });
        }
      } else {
        this.snackBar.open('Failed to cancel the meeting. Please try again.', 'OK', {
          duration: 3000,
          panelClass: 'error-snackbar'
        });
      }
    } finally {
      this.submitting = false;
    }
  }

  private async submitWithRecaptcha(version: 'v3' | 'v2', providedToken?: string): Promise<void> {
    let captchaToken: string | null = null;

    // Get captcha token
    if (this.recaptchaRequired) {
      if (version === 'v3' && this.recaptchaInitialized) {
        captchaToken = await this.recaptchaV3Service.execute('cancel_meeting');
      } else {
        captchaToken = providedToken || null;
      }

      if (!captchaToken) {
        throw new Error('No captcha token available');
      }
    }

    // Get the message from the form
    const message = this.cancellationForm.get('message')?.value || '';

    // Call API with the cancellation data and captcha token
    const response = await firstValueFrom(this.meetingService.cancelMeeting({
      meetingId: this.meetingId,
      cancelId: this.cancelId,
      recaptchaToken: captchaToken,
      recaptchaAction: 'cancel_meeting',
      recaptchaVersion: version, // Include version info
      message: message
    }));
    
    this.cancellationSubmitted = true;
  }
}